import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { Col, Row, Tabs, Button, Tooltip } from 'antd'
import { ReadOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import { PowerBIEmbed } from 'powerbi-client-react'
import { models } from 'powerbi-client'
import { getDashboardServices } from '../../Redux/Services/Administrator/Dashboard'
import '../../App.css'
import '../../Styles/Routes/Deck.css'

const Deck = () => {
    const [activeTab, setActiveTab] = useState('lectura')
    const [reloadKey, setReloadKey] = useState(0)
    const reportRef = useRef(null)
    const reportDescargaRef = useRef(null)

    const {
        rex_data_dashboard,
    } = useSelector(state => state.dashboard)

    const dispatch = useDispatch()

    const dashboardData = useQuery({
        queryKey: ['dashboard'],
        queryFn: () => getDashboardServices(dispatch),
        retry: 1,
        refetchOnWindowFocus: false,
    })

    console.log('Dashboard data:', JSON.stringify(rex_data_dashboard, null, 2))
    console.log('Public URL:', rex_data_dashboard?.publico?.url)
    console.log('Private data:', rex_data_dashboard?.privado)

    // Configuración para PowerBI Embed (solo para tab descarga/privado)
    const getPrivateEmbedConfig = () => {
        const privateData = rex_data_dashboard?.privado
        console.log('Private config data:', privateData)

        if (!privateData || !privateData.token || !privateData.workspaceId || !privateData.reportId) {
            console.log('Missing private data for PowerBI embed')
            return null
        }

        const { token, workspaceId, reportId } = privateData

        return {
            type: 'report',
            id: reportId,
            embedUrl: `https://app.powerbi.com/reportEmbed?reportId=${reportId}&groupId=${workspaceId}`,
            accessToken: token,
            tokenType: models.TokenType.Embed,
            settings: {
                panes: {
                    filters: { expanded: false, visible: false },
                    pageNavigation: { visible: false },
                },
                background: models.BackgroundType.Transparent,
                layoutType: models.LayoutType.FitToWidth,
                customLayout: { displayOption: models.DisplayOption.FitToPage },
            },
        }
    }

    // URL público para iframe (tab lectura)
    const getPublicUrl = () => {
        const publicUrl = rex_data_dashboard?.publico?.url
        console.log('Public URL:', publicUrl)
        return publicUrl || null
    }

    // Manejadores de eventos
    const handleEmbedded = () => {
        console.log('Report embedded successfully')
    }

    const handleError = (error) => {
        console.error('PowerBI embed error:', error)
    }

    const handleLoaded = () => {
        console.log('Report loaded successfully')
    }

    const handleRendered = () => {
        console.log('Report rendered successfully')
    }

    const handleTabChange = (key) => {
        setActiveTab(key)
        // Forzar re-renderizado cuando se cambie de tab
        setTimeout(() => {
            setReloadKey(prev => prev + 1)
        }, 100)
    }

    const handleReload = () => {
        console.log('Reloading BI dashboard...')
        setReloadKey(prev => prev + 1)

        // Limpiar referencias anteriores
        if (window.report) {
            try {
                window.report = null
            } catch (error) {
                console.log('Error clearing report reference:', error)
            }
        }

        if (window.reportDescarga) {
            try {
                window.reportDescarga = null
            } catch (error) {
                console.log('Error clearing reportDescarga reference:', error)
            }
        }
    }

    // Efecto para limpiar al desmontar el componente
    useEffect(() => {
        return () => {
            if (window.report) window.report = null
            if (window.reportDescarga) window.reportDescarga = null
        }
    }, [])

    const privateEmbedConfig = getPrivateEmbedConfig()
    const publicUrl = getPublicUrl()

    // Configuración de los tabs
    const tabItems = [
        {
            key: 'lectura',
            label: (
                <span>
                    <ReadOutlined className="tab-icon" />
                    Lectura
                </span>
            ),
            children: (
                <div className="deck-tab-pane">
                    {dashboardData.isLoading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <div className="loading-text">Cargando información de lectura...</div>
                        </div>
                    ) : publicUrl ? (
                        <div className="powerbi-container" key={`public-${reloadKey}`}>
                            <iframe
                                title="Dashboard de Lectura"
                                src={publicUrl}
                                width="100%"
                                height="100%"
                                frameBorder="0"
                                allowFullScreen
                                style={{ borderRadius: '12px' }}
                                key={`iframe-${reloadKey}`}
                            />
                        </div>
                    ) : (
                        <div className="loading-container">
                            <div className="loading-text">No hay datos de lectura disponibles</div>
                        </div>
                    )}
                </div>
            )
        },
        {
            key: 'descarga',
            label: (
                <span>
                    <DownloadOutlined className="tab-icon" />
                    Descarga
                </span>
            ),
            children: (
                <div className="deck-tab-pane">
                    {dashboardData.isLoading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <div className="loading-text">Cargando información de descarga...</div>
                        </div>
                    ) : privateEmbedConfig ? (
                        <div className="powerbi-container" key={`private-${reloadKey}`}>
                            <PowerBIEmbed
                                key={`powerbi-${reloadKey}`}
                                embedConfig={privateEmbedConfig}
                                eventHandlers={
                                    new Map([
                                        ['loaded', handleLoaded],
                                        ['rendered', handleRendered],
                                        ['error', handleError],
                                        ['embedded', handleEmbedded]
                                    ])
                                }
                                cssClassName="power-bi-embed"
                                style={{ height: "100%" }}
                                getEmbeddedComponent={(embeddedReport) => {
                                    window.reportDescarga = embeddedReport
                                    reportDescargaRef.current = embeddedReport
                                }}
                            />
                        </div>
                    ) : (
                        <div className="loading-container">
                            <div className="loading-text">No hay datos de descarga disponibles</div>
                        </div>
                    )}
                </div>
            )
        }
    ]

    return (
        <div className="deck-container">
            <Row>
                <Col span={24}>
                    <div className="deck-header">
                        <div className="deck-header-content">
                            <div className="deck-header-text">
                                <h1 className="deck-title">DECK</h1>
                                <p className="deck-subtitle">Panel de control y análisis de datos</p>
                            </div>
                            <div className="deck-header-actions">
                                <Tooltip title="Recargar dashboard">
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={<ReloadOutlined />}
                                        onClick={handleReload}
                                        className="reload-button"
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                </Col>
                <Col span={24}>
                    <div className="deck-tabs-container">
                        <Tabs
                            activeKey={activeTab}
                            onChange={handleTabChange}
                            items={tabItems}
                            className="deck-tabs"
                            tabBarGutter={0}
                            size="large"
                        />
                    </div>
                </Col>
            </Row>
        </div>
    )
}

export default Deck